import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as GenerateIcon,
  CheckCircle as ApproveIcon,
  Shuffle as ShuffleIcon,
} from '@mui/icons-material';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { usePermissions, useCanManageLeague } from '../../../contexts/PermissionsContext';
import ScheduleConfigForm from '../../../components/scheduleGenerator/ScheduleConfigForm';
import SchedulePreview from '../../../components/scheduleGenerator/SchedulePreview';
import ApproveScheduleDialog from '../../../components/scheduleGenerator/ApproveScheduleDialog';
import {
  createNewForm,
  draftToForm,
  formToPayload,
  isFormUnchanged,
  validateForm,
} from '../../../components/scheduleGenerator/scheduleFormModel';
import { formatTimestampDate } from '../../../utils/scheduleFormat';

const sortByName = (items) => [...items].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

const ScheduleDraftPage = () => {
  const { leagueId, draftId } = useParams();
  const navigate = useNavigate();
  const isNew = draftId === 'new';
  const numericLeagueId = parseInt(leagueId, 10);
  const { isLoading: permissionsLoading } = usePermissions();
  const canManageLeague = useCanManageLeague(numericLeagueId);

  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [venues, setVenues] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [refsLoaded, setRefsLoaded] = useState(false);
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [draftLoading, setDraftLoading] = useState(!isNew);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const loadedDraftIdRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await unifiedApi.getScheduleSlotTemplates();
      setTemplates(sortByName(data || []));
    } catch (err) {
      console.error('Failed to load slot templates', err);
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    if (!canManageLeague || !leagueId) return undefined;
    let cancelled = false;
    setRefsLoaded(false);
    (async () => {
      try {
        const [leagueData, teamData, venueData] = await Promise.all([
          unifiedApi.getLeagueById(leagueId),
          unifiedApi.getTeams(leagueId),
          unifiedApi.getVenues(),
        ]);
        if (cancelled) return;
        setLeague(leagueData);
        setTeams(sortByName((teamData || []).filter((t) => String(t.leagueId ?? leagueId) === String(leagueId))));
        setVenues(sortByName(venueData || []));
        await loadTemplates();
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load league data.');
      } finally {
        if (!cancelled) setRefsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [canManageLeague, leagueId, loadTemplates]);

  const loadDraft = useCallback(async (id) => {
    setDraftLoading(true);
    setLoadError(null);
    try {
      const data = await unifiedApi.getScheduleDraft(leagueId, id);
      if (!data) {
        setLoadError('Schedule draft not found.');
        setDraft(null);
        return;
      }
      loadedDraftIdRef.current = data.id;
      setDraft(data);
      setForm(draftToForm(data));
    } catch (err) {
      setLoadError(err?.message || 'Failed to load the schedule draft.');
    } finally {
      setDraftLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (!canManageLeague) return;
    if (isNew) {
      loadedDraftIdRef.current = null;
      setDraft(null);
      setForm(null);
      setDraftLoading(false);
      return;
    }
    if (loadedDraftIdRef.current === Number(draftId)) return;
    loadDraft(draftId);
  }, [canManageLeague, draftId, isNew, loadDraft]);

  useEffect(() => {
    if (isNew && refsLoaded && form === null) {
      setForm(createNewForm({
        name: league?.name ? `${league.name} regular season` : 'Regular season',
        teamIds: teams.map((t) => t.id),
      }));
    }
  }, [isNew, refsLoaded, form, league, teams]);

  const isApproved = draft?.status === 'Approved';
  const isDirty = !draft || !isFormUnchanged(form, draft);

  const handleGenerate = async () => {
    setActionError(null);
    setSuccess(null);
    const validationError = validateForm(form);
    if (validationError) {
      setActionError(validationError);
      return;
    }
    setBusy('generate');
    try {
      const payload = formToPayload(form);
      const saved = isNew || !draft
        ? await unifiedApi.createScheduleDraft(leagueId, payload)
        : await unifiedApi.updateScheduleDraft(leagueId, draft.id, payload);
      loadedDraftIdRef.current = saved.id;
      setDraft(saved);
      setForm(draftToForm(saved));
      setSuccess('Schedule generated.');
      if (isNew) {
        navigate(`/admin/leagues/${leagueId}/schedule-generator/${saved.id}`, { replace: true });
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to generate the schedule.');
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async () => {
    if (!draft) return;
    setActionError(null);
    setSuccess(null);
    setBusy('regenerate');
    try {
      const saved = await unifiedApi.regenerateScheduleDraft(leagueId, draft.id);
      setDraft(saved);
      setForm(draftToForm(saved));
      setSuccess('New variation generated.');
    } catch (err) {
      setActionError(err?.message || 'Failed to regenerate the schedule.');
    } finally {
      setBusy(null);
    }
  };

  const handleApproved = () => {
    loadedDraftIdRef.current = null;
    loadDraft(draft.id);
  };

  const teamLinks = useMemo(() => teams.map((t) => ({ id: t.id, name: t.name, slug: t.slug })), [teams]);

  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(`/admin/leagues/${leagueId}/schedule-generator`)}
      sx={{ mb: 2 }}
    >
      Back to Schedule Generator
    </Button>
  );

  if (permissionsLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!canManageLeague) {
    return (
      <AdminLayout>
        <Alert severity="error">You are not authorized to manage this league&apos;s schedule.</Alert>
      </AdminLayout>
    );
  }

  const loading = !refsLoaded || draftLoading || (!loadError && form === null);

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        {backButton}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, wordBreak: 'break-word' }}>
            {isNew ? 'New schedule' : (draft?.name || 'Schedule draft')}
          </Typography>
          {draft && (
            <Chip
              label={draft.status}
              color={isApproved ? 'success' : 'default'}
              size="small"
              clickable={false}
              onClick={() => {}}
            />
          )}
        </Stack>
        {league?.name && (
          <Typography variant="body1" color="text.secondary">{league.name}</Typography>
        )}
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 3 }}>{loadError}</Alert>}

      {loading && !loadError ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : form && (
        <Stack spacing={3}>
          {isApproved && (
            <Alert severity="success">
              {`Approved on ${formatTimestampDate(draft.approvedAt)} — ${draft.approvedGameCount ?? 0} games created`}
              {draft.replacedGameCount ? `, ${draft.replacedGameCount} replaced` : ''}
              {draft.result?.tbdGameCount > 0 ? `, ${draft.result.tbdGameCount} TBD games added` : ''}
              {'. This schedule can no longer be edited.'}
              {league?.slug && (
                <>
                  {' '}
                  <Link component={RouterLink} to={`/leagues/${league.slug}/schedule`}>View league schedule</Link>
                </>
              )}
            </Alert>
          )}

          <ScheduleConfigForm
            form={form}
            onChange={setForm}
            teams={teams}
            venues={venues}
            templates={templates}
            onTemplatesChanged={loadTemplates}
            disabled={isApproved || Boolean(busy)}
          />

          {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

          {!isApproved && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
              <Button
                variant="contained"
                startIcon={busy === 'generate' ? <CircularProgress size={18} color="inherit" /> : <GenerateIcon />}
                onClick={handleGenerate}
                disabled={Boolean(busy)}
              >
                {busy === 'generate' ? 'Generating…' : 'Generate'}
              </Button>
              {draft && (
                <Button
                  variant="outlined"
                  startIcon={busy === 'regenerate' ? <CircularProgress size={18} color="inherit" /> : <ShuffleIcon />}
                  onClick={handleRegenerate}
                  disabled={Boolean(busy) || isDirty}
                >
                  {busy === 'regenerate' ? 'Generating…' : 'Regenerate (new variation)'}
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => setApproveOpen(true)}
                disabled={!draft || isDirty || Boolean(busy)}
              >
                Approve…
              </Button>
              {draft && isDirty && (
                <Typography variant="body2" color="warning.dark">
                  You have unsaved changes — click Generate to update the preview before approving.
                </Typography>
              )}
            </Stack>
          )}

          {busy && <LinearProgress />}

          {draft?.result && <SchedulePreview draft={draft} teams={teamLinks} />}
        </Stack>
      )}

      {draft && (
        <ApproveScheduleDialog
          open={approveOpen}
          onClose={() => setApproveOpen(false)}
          leagueId={leagueId}
          draft={draft}
          onApproved={handleApproved}
        />
      )}
    </AdminLayout>
  );
};

export default ScheduleDraftPage;
